import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { ROUTES } from '../routes';

interface LimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  primaryLabel?: string;
  secondaryLabel?: string;
}

export function LimitModal({
  isOpen,
  onClose,
  title,
  message,
  primaryLabel,
  secondaryLabel,
}: LimitModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mouseDownOnOverlayRef = useRef(false);

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Trigger enter transition on next frame
      const raf = requestAnimationFrame(() => setMounted(true));
      return () => {
        cancelAnimationFrame(raf);
        document.removeEventListener('keydown', handleEscape);
        setMounted(false);
      };
    }
    setMounted(false);
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 transition-opacity duration-200"
      onMouseDown={(e) => {
        mouseDownOnOverlayRef.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        if (mouseDownOnOverlayRef.current && e.target === e.currentTarget) {
          onClose();
        }
        mouseDownOnOverlayRef.current = false;
      }}
    >
      <div
        className="bg-zinc-900 border border-zinc-600/40 rounded-2xl shadow-2xl shadow-black/60 w-full max-w-[420px] p-8 transition-all duration-200"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'scale(1)' : 'scale(0.95)',
        }}
      >
        {/* Icon */}
        <div className="flex justify-center mb-5">
          <div className="w-12 h-12 rounded-full bg-indigo-500/15 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-indigo-400" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-lg font-bold text-zinc-100 text-center mb-3">{title}</h2>

        {/* Message */}
        <p className="text-sm text-zinc-400 text-center leading-relaxed mb-8">{message}</p>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate(ROUTES.subscription);
            }}
            className="w-full py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition-colors cursor-pointer"
          >
            {primaryLabel ?? t('limits.see_plans')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 px-4 rounded-xl border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-zinc-200 text-sm font-medium transition-colors cursor-pointer"
          >
            {secondaryLabel ?? t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
